import React, { useRef, memo, useEffect, useMemo, useState } from 'react'
import { Calendar } from 'primereact/calendar'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import classNames from 'classnames'
import { JsonView as JsonViewLite, defaultStyles } from 'react-json-view-lite'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { Checkbox } from 'primereact/checkbox'
import { Button } from 'primereact/button'
import { subDays } from 'date-fns'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'

import { MultiSelect } from '../../uikit/MultiSelect'
import { SignInArrowIcon } from '../../../lib/icons/sign-in-arrow-icon'
import { theme } from '../../../lib/theme'
import { Table } from '../../uikit/Table'
import useText from '../../hooks/useText'
import localizeDate from '../../utils/localizeDate'
import { OcppLogsEntry } from '../../redux/api/web-api'
import { LOG_TYPES_PARTNER, LOG_TYPES_SUPPORT, LogTypes, OCPP_LOGS_MAX_AGE_DAYS } from '../../constants/LogTypes'
import { isSupportDomain } from '../../../lib/currentDomain'
import safeJsonParse from '../../../lib/safe-json-parse'
import { useToast } from '../../uikit/ToastProvider'

const JsonView = memo(
    ({ message }: { message: string }) => {
        const processedData = useMemo(() => {
            const data = safeJsonParse<{ data?: string } | null>(message, null)
            return data?.data ? { ...data, data: safeJsonParse(data.data, data.data) } : data
        }, [message])

        return processedData === null ? (
            <>{message?.replace(/\0.*$/g, '')}</>
        ) : (
            <JsonViewLite
                data={processedData}
                style={{
                    ...defaultStyles,
                    container: 'json-view',
                    label: 'json-view__label',
                    stringValue: 'json-view__stringValue',
                    numberValue: 'json-view__numberValue',
                    booleanValue: 'json-view__booleanValue',
                    punctuation: 'json-view__punctuation',
                    clickableLabel: 'json-view__clickableLabel',
                }}
                shouldExpandNode={(level, value) => {
                    if (isEmpty(value)) {
                        return false
                    }
                    if (level === 0 || level >= 2) {
                        return true
                    }
                    return false
                }}
                clickToExpandNode
            />
        )
    },
    (prevProps, nextProps) => {
        return prevProps.message === nextProps.message
    }
)

const DEFAULT_UNSELECTED_FILTERS: Array<LogTypes> = isSupportDomain
    ? [LogTypes.Heartbeat, LogTypes.MeterValues]
    : [LogTypes.StatusNotification]

const defaultActions = (isSupportDomain ? LOG_TYPES_SUPPORT : LOG_TYPES_PARTNER).map((type) => LogTypes[type])

const defaultActionFilter = defaultActions.filter(
    (type) => !DEFAULT_UNSELECTED_FILTERS.map((type) => LogTypes[type]).includes(type)
)

const actionFilterOptions = LOG_TYPES_SUPPORT.map((type) => ({
    label: LogTypes[type],
    value: LogTypes[type],
}))

export interface OcppLogsTableFilters {
    actions?: string
    errorsOnly?: boolean
    from?: string
    to?: string
}

interface OcppLogsFormData {
    actions: string[]
    errorsOnly: boolean
    from: Date | null
    to: Date | null
}

export const OcppLogsTable = ({
    logs,
    isLoading,
    isFetching,
    refetch,
    onFiltersChange,
    onLoadMore,
    hasMore,
    height = 'calc(100vh - 400px)',
    'data-testid': testId,
    filters,
}: {
    logs?: OcppLogsEntry[]
    isLoading?: boolean
    isFetching?: boolean
    refetch?: () => void
    onFiltersChange?: (filters: OcppLogsTableFilters) => void
    onLoadMore?: () => void
    hasMore?: boolean
    'data-testid'?: string
    height?: string
    filters?: OcppLogsTableFilters
}) => {
    const text = useText()
    const toast = useToast()
    const dt = useRef<DataTable<OcppLogsEntry[]>>(null)

    const selectedActions = filters?.actions ? filters.actions.split(',') : defaultActionFilter

    // Capture the initial filters only once - these are the true "reset" defaults
    const initialFormValues = useMemo(
        () => ({
            actions: filters?.actions ? filters.actions.split(',') : defaultActionFilter,
            errorsOnly: filters?.errorsOnly || false,
            from: filters?.from ? new Date(filters.from) : null,
            to: filters?.to ? new Date(filters.to) : null,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    // Current form values should match the current filters for display
    const currentFormValues = useMemo(
        () => ({
            actions: filters?.actions ? filters.actions.split(',') : [],
            errorsOnly: filters?.errorsOnly || false,
            from: filters?.from ? new Date(filters.from) : null,
            to: filters?.to ? new Date(filters.to) : null,
        }),
        [filters]
    )

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isDirty },
    } = useForm<OcppLogsFormData>({
        mode: 'onChange',
        defaultValues: initialFormValues,
    })

    const watchedValues = watch()
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isResetting, setIsResetting] = useState(false)
    const [isApplyingFilters, setIsApplyingFilters] = useState(false)

    const isAtInitialState = useMemo(() => {
        const sortedCurrent = [...(watchedValues.actions || [])].sort()
        const sortedInitial = [...initialFormValues.actions].sort()

        return (
            isEqual(sortedCurrent, sortedInitial) &&
            watchedValues.errorsOnly === initialFormValues.errorsOnly &&
            watchedValues.from?.getTime() === initialFormValues.from?.getTime() &&
            watchedValues.to?.getTime() === initialFormValues.to?.getTime()
        )
    }, [watchedValues, initialFormValues])

    useEffect(() => {
        reset(currentFormValues, {
            keepDirty: false,
            keepDirtyValues: false,
        })
    }, [currentFormValues, reset])

    // Turn off loading states when fetching completes
    useEffect(() => {
        if (!isFetching) {
            if (isApplyingFilters) {
                setIsApplyingFilters(false)
            }
            if (isResetting) {
                setIsResetting(false)
            }
            if (isRefreshing) {
                setIsRefreshing(false)
            }
        }
    }, [isFetching, isApplyingFilters, isResetting, isRefreshing])

    const handleReset = async () => {
        setIsResetting(true)
        // Reset to the initial values (very first filters when component mounted)
        reset(initialFormValues)

        // Convert form values back to filter format for the API
        const resetFilters: OcppLogsTableFilters = {
            actions: initialFormValues.actions.length > 0 ? initialFormValues.actions.join(',') : undefined,
            errorsOnly: initialFormValues.errorsOnly,
            from: initialFormValues.from?.toISOString(),
            to: initialFormValues.to?.toISOString(),
        }
        onFiltersChange?.(resetFilters)
    }

    const onSubmit: SubmitHandler<OcppLogsFormData> = async (data) => {
        setIsApplyingFilters(true)

        const filters: OcppLogsTableFilters = {
            actions: data.actions.length > 0 ? data.actions.join(',') : undefined,
            errorsOnly: data.errorsOnly,
            from: data.from?.toISOString(),
            to: data.to?.toISOString(),
        }

        onFiltersChange?.(filters)
    }

    const handleRefresh = async () => {
        if (typeof refetch !== 'function' || isRefreshing) {
            return
        }
        setIsRefreshing(true)
        refetch()
    }

    const emptyMessage = () => {
        if (!selectedActions.length) {
            return text('msgSelectFiltersToViewLogs')
        }
        return text('msgNoLogsForCurrentSelection')
    }

    const directionLabels: Record<string, string> = {
        Sent: text('lblOcppLogDirectionSent'),
        Recv: text('lblOcppLogDirectionReceived'),
    }

    return (
        <div className="flex flex-column" data-testid={`${testId}-table-container`}>
            <div className="surface-card p-4 border-round" data-testid={`${testId}-filter-container`}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex flex-column md:flex-row gap-4 align-items-start md:align-items-center justify-content-end">
                        <div className="flex align-items-center">
                            <Controller
                                name="errorsOnly"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Checkbox
                                        inputId={`${testId}-errorsOnly`}
                                        checked={value}
                                        onChange={(e) => onChange(!!e.checked)}
                                        className="mr-2"
                                    />
                                )}
                            />
                            <label htmlFor={`${testId}-errorsOnly`} className="ml-1 mb-0 cursor-pointer">
                                {text('lblShowErrorsOnly')}
                            </label>
                        </div>
                        <div className="w-12rem">
                            <Controller
                                name="actions"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <MultiSelect
                                        filter
                                        inputId="actionFilter"
                                        options={actionFilterOptions}
                                        value={value}
                                        onChange={(e) => onChange(e.value)}
                                        className="w-full"
                                        data-testid={`${testId}-actionFilter`}
                                        selectAll
                                        selectAllLabel={text('btnSelectAll')}
                                        filterPlaceholder={text('lblSearch')}
                                        placeholder={text('lblAction')}
                                        aria-label={text('lblAction')}
                                        showSelectAll
                                        testId={testId}
                                    />
                                )}
                            />
                        </div>
                        <div className="w-12rem">
                            <Controller
                                name="from"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Calendar
                                        value={value}
                                        onChange={(e) => onChange(e.value)}
                                        readOnlyInput
                                        showTime
                                        hourFormat="24"
                                        inputId={`${testId}-dateFrom`}
                                        maxDate={watchedValues.to || new Date()}
                                        showButtonBar
                                        minDate={subDays(new Date(), OCPP_LOGS_MAX_AGE_DAYS)}
                                        className="w-full"
                                        dateFormat="yy-mm-dd"
                                        placeholder={text('lblFromField')}
                                        ariaLabel={text('lblFromField')}
                                    />
                                )}
                            />
                        </div>
                        <div className="w-12rem">
                            <Controller
                                name="to"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Calendar
                                        value={value}
                                        onChange={(e) => onChange(e.value)}
                                        readOnlyInput
                                        showTime
                                        hourFormat="24"
                                        inputId={`${testId}-dateTo`}
                                        minDate={watchedValues.from || subDays(new Date(), OCPP_LOGS_MAX_AGE_DAYS)}
                                        showButtonBar
                                        maxDate={new Date()}
                                        className="w-full"
                                        dateFormat="yy-mm-dd"
                                        placeholder={text('lblToField')}
                                        ariaLabel={text('lblToField')}
                                    />
                                )}
                            />
                        </div>
                        <div className="flex align-items-center gap-2">
                            <Button size="small" type="submit" disabled={!isDirty} loading={isApplyingFilters}>
                                {text('btnApply')}
                            </Button>

                            <Button
                                icon="pi pi-times"
                                onClick={handleReset}
                                rounded
                                tooltip={text('btnReset')}
                                loading={isResetting}
                                disabled={isAtInitialState}
                                tooltipOptions={{ position: 'top' }}
                                text
                                data-testid={`${testId}-reset-button`}
                            />
                            <Button
                                icon="pi pi-refresh"
                                onClick={handleRefresh}
                                rounded
                                tooltip={text('btnRefresh')}
                                loading={isRefreshing}
                                tooltipOptions={{ position: 'top' }}
                                text
                                data-testid={`${testId}-refresh-button`}
                            />
                        </div>
                    </div>
                </form>
            </div>

            <Table
                emptyMessage={emptyMessage}
                scrollable
                scrollHeight={height}
                ref={dt}
                dataKey="id"
                value={logs || []}
                loading={isLoading || isApplyingFilters || isResetting || isRefreshing}
                fetching={isFetching}
                onLoadMoreData={onLoadMore}
                hasMoreData={hasMore}
                data-testid={`${testId}-table`}
            >
                <Column field="timestamp" header={text('lblTimestamp')} body={(log) => localizeDate(log.timestamp)} />
                <Column
                    field="ocppAction"
                    header={text('lblAction')}
                    body={(log) => {
                        const isOutgoing = log.direction === 'Sent'
                        return (
                            <span title={directionLabels[log.direction] ?? text('lblUnknown')} className="inline-flex">
                                <SignInArrowIcon
                                    color={isOutgoing ? theme.brandColors.CA_Mid_Blue : theme.brandColors.CA_Green}
                                    className={classNames({ 'rotate-180': isOutgoing }, 'm-0', 'mr-3')}
                                />
                                {log.ocppAction}
                            </span>
                        )
                    }}
                />

                <Column
                    field="payloadJson"
                    header={text('lblMessage')}
                    body={(log) => <JsonView message={log.payloadJson} />}
                />

                <Column
                    body={(log) => (
                        <Button
                            icon="pi pi-copy"
                            rounded
                            severity="info"
                            size="small"
                            outlined
                            onClick={() => {
                                navigator.clipboard.writeText(log.payloadJson)
                                toast.notifySuccess(text('msgOcppLogsCopy'))
                            }}
                        />
                    )}
                />
            </Table>
        </div>
    )
}
